import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

const initialForm = {
  name: "",
  slug: "",
  info: "",
  icon: "",
  feature_flags: "{}",
  is_active: true,
  display_order: "",
};

const RiteCategories = () => {
  const token = getDecryptedAccessToken();

  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [openAddEdit, setOpenAddEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [mode, setMode] = useState("add"); // add | edit
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.get("/api/v1/clients/categories/", authHeaders);
      setCategories(Array.isArray(res.data.results) ? res.data.results : []);
    } catch (error) {
      showSnackbar("Failed to load categories", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;

    return categories.filter((item) => {
      return (
        item.name?.toLowerCase().includes(q) ||
        item.slug?.toLowerCase().includes(q) ||
        item.info?.toLowerCase().includes(q) ||
        item.icon?.toLowerCase().includes(q)
      );
    });
  }, [categories, search]);

  const resetForm = () => {
    setForm(initialForm);
    setErrors({});
    setSelectedCategory(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setMode("add");
    setOpenAddEdit(true);
  };

  const handleOpenEdit = (category) => {
    setMode("edit");
    setSelectedCategory(category);
    setErrors({});
    setForm({
      name: category.name || "",
      slug: category.slug || "",
      info: category.info || "",
      icon: category.icon || "",
      feature_flags: JSON.stringify(category.feature_flags || {}, null, 2),
      is_active: !!category.is_active,
      display_order:
        category.display_order === 0 || category.display_order
          ? String(category.display_order)
          : "",
    });
    setOpenAddEdit(true);
  };

  const handleOpenDelete = (category) => {
    setSelectedCategory(category);
    setOpenDelete(true);
  };

  const handleCloseAddEdit = () => {
    if (submitting) return;
    setOpenAddEdit(false);
    resetForm();
  };

  const handleCloseDelete = () => {
    if (submitting) return;
    setOpenDelete(false);
    setSelectedCategory(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const slugify = (value) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/['"]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleNameBlur = () => {
    if (mode === "add" && !form.slug.trim() && form.name.trim()) {
      handleChange("slug", slugify(form.name));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = "Name is required";
    } else if (form.name.trim().length > 255) {
      nextErrors.name = "Name is too long";
    }

    if (!form.slug.trim()) {
      nextErrors.slug = "Slug is required";
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug.trim())) {
      nextErrors.slug =
        "Slug must contain lowercase letters, numbers, and hyphens only";
    }

    if (form.display_order !== "") {
      const parsedOrder = Number(form.display_order);
      if (Number.isNaN(parsedOrder) || parsedOrder < 0) {
        nextErrors.display_order = "Display order must be 0 or greater";
      }
    }

    if (form.feature_flags.trim()) {
      try {
        const parsed = JSON.parse(form.feature_flags);
        if (
          parsed === null ||
          Array.isArray(parsed) ||
          typeof parsed !== "object"
        ) {
          nextErrors.feature_flags = "Feature flags must be a valid JSON object";
        }
      } catch (e) {
        nextErrors.feature_flags = "Feature flags must be valid JSON";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => {
    let parsedFeatureFlags = {};
    if (form.feature_flags.trim()) {
      parsedFeatureFlags = JSON.parse(form.feature_flags);
    }

    return {
      name: form.name.trim(),
      slug: form.slug.trim(),
      info: form.info.trim(),
      icon: form.icon.trim(),
      feature_flags: parsedFeatureFlags,
      is_active: !!form.is_active,
      display_order: form.display_order === "" ? 0 : Number(form.display_order),
    };
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const payload = buildPayload();

      if (mode === "add") {
        await ApiServices.post("/api/v1/clients/categories/", payload, authHeaders);
        showSnackbar("Category created successfully", "success");
      } else if (selectedCategory?.id) {
        await ApiServices.patch(
          `/api/v1/clients/categories/${selectedCategory.id}/`,
          payload,
          authHeaders
        );
        showSnackbar("Category updated successfully", "success");
      }

      setOpenAddEdit(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.slug?.[0] ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        `Failed to ${mode === "add" ? "create" : "update"} category`;

      showSnackbar(apiMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory?.id) return;

    try {
      setSubmitting(true);
      await ApiServices.delete(
        `/api/v1/clients/categories/${selectedCategory.id}/`,
        authHeaders
      );
      showSnackbar("Category deleted successfully", "success");
      setOpenDelete(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.detail || "Failed to delete category";
      showSnackbar(apiMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
            mb={3}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Categories
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Manage client business categories
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                placeholder="Search by name, slug, info..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 320 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
              >
                Add Category
              </Button>
            </Stack>
          </Stack>

          {loading ? (
            <Box py={6} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : filteredCategories.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography variant="body1" fontWeight={600}>
                No categories found
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Try a different search or create a new category.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredCategories.map((category) => (
                <Grid item xs={12} md={6} lg={4} key={category.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                    }}
                  >
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6">
                              {category.icon || "📁"}
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                              {category.name || "-"}
                            </Typography>
                          </Stack>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.75, wordBreak: "break-word" }}
                          >
                            Slug: {category.slug || "-"}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEdit(category)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDelete(category)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 2 }}
                      >
                        <Chip
                          size="small"
                          label={category.is_active ? "Active" : "Inactive"}
                          color={category.is_active ? "success" : "default"}
                        />
                        <Chip
                          size="small"
                          label={`Order: ${
                            category.display_order === 0 || category.display_order
                              ? category.display_order
                              : "-"
                          }`}
                          variant="outlined"
                        />
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          minHeight: 44,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {category.info || "No description provided."}
                      </Typography>

                      <Box mt={2}>
                        <Typography variant="caption" color="text.secondary">
                          Feature Flags
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.75,
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: "#f7f7f7",
                            fontFamily: "monospace",
                            fontSize: 12,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {JSON.stringify(category.feature_flags || {}, null, 2)}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={openAddEdit}
        onClose={handleCloseAddEdit}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {mode === "add" ? "Add Category" : "Edit Category"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                fullWidth
                required
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onBlur={handleNameBlur}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Slug"
                fullWidth
                required
                value={form.slug}
                onChange={(e) =>
                  handleChange("slug", slugify(e.target.value))
                }
                error={!!errors.slug}
                helperText={errors.slug || "Example: restaurant"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Icon"
                fullWidth
                value={form.icon}
                onChange={(e) => handleChange("icon", e.target.value)}
                helperText="Example: 🍽️"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Display Order"
                type="number"
                fullWidth
                value={form.display_order}
                onChange={(e) => handleChange("display_order", e.target.value)}
                error={!!errors.display_order}
                helperText={errors.display_order}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Info"
                fullWidth
                multiline
                minRows={3}
                value={form.info}
                onChange={(e) => handleChange("info", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Feature Flags (JSON Object)"
                fullWidth
                multiline
                minRows={6}
                value={form.feature_flags}
                onChange={(e) => handleChange("feature_flags", e.target.value)}
                error={!!errors.feature_flags}
                helperText={
                  errors.feature_flags || 'Example: { "booking": true }'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) =>
                      handleChange("is_active", e.target.checked)
                    }
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAddEdit} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? mode === "add"
                ? "Saving..."
                : "Updating..."
              : mode === "add"
              ? "Save"
              : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Category</DialogTitle>

        <DialogContent dividers>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            Are you sure you want to delete this category?
          </Typography>

          {selectedCategory && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "#fafafa",
              }}
            >
              <Typography variant="body2">
                <strong>Name:</strong> {selectedCategory.name || "-"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Slug:</strong> {selectedCategory.slug || "-"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Info:</strong> {selectedCategory.info || "-"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Status:</strong>{" "}
                {selectedCategory.is_active ? "Active" : "Inactive"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Display Order:</strong>{" "}
                {selectedCategory.display_order === 0 ||
                selectedCategory.display_order
                  ? selectedCategory.display_order
                  : "-"}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will remove the category. Any client details using it will have
            category set to null.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={submitting}
          >
            {submitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiteCategories;